import React from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * Pagination — แสดงจำนวนรวมเสมอ (เจ้าหน้าที่ต้องรู้ว่าคิวเหลือเท่าไร ไม่ใช่แค่หน้านี้)
 * และให้เลือกจำนวนแถวต่อหน้าได้
 */
export interface PaginationProps {
  page: number;            // เริ่มที่ 1
  pageSize: number;
  totalItems: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** ปิดปุ่มระหว่างโหลดข้อมูลหน้าใหม่ */
  busy?: boolean;
  className?: string;
}

const btn =
  'inline-flex min-h-[var(--hit-control)] min-w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] disabled:cursor-not-allowed disabled:border-[var(--line-200)] disabled:text-[var(--text-disabled)] disabled:hover:bg-[var(--surface-0)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2';

export const Pagination: React.FC<PaginationProps> = ({
  page, pageSize, totalItems,
  pageSizeOptions = [25, 50, 100, 200],
  onPageChange, onPageSizeChange, busy = false, className,
}) => {
  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(totalItems, page * pageSize);
  const fmt = (n: number) => n.toLocaleString('th-TH');

  return (
    <nav
      aria-label="การแบ่งหน้า"
      className={cn(
        'flex flex-wrap items-center justify-between gap-[var(--space-3)] border-t border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-form)]',
        className,
      )}
    >
      <p className="text-[var(--ink-700)]">
        แสดง <span className="bo-num font-[var(--weight-semibold)]">{fmt(from)}–{fmt(to)}</span>{' '}
        จากทั้งหมด <span className="bo-num font-[var(--weight-semibold)]">{fmt(totalItems)}</span> รายการ
      </p>

      <div className="flex items-center gap-[var(--space-3)]">
        <label className="flex items-center gap-[var(--space-2)] text-[var(--ink-700)]">
          แถวต่อหน้า
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>

        <div className="flex items-center gap-[var(--space-1)]">
          <button type="button" className={btn} disabled={busy || page <= 1} onClick={() => onPageChange(1)} aria-label="หน้าแรก">
            <Icon name="first_page" size={18} />
          </button>
          <button type="button" className={btn} disabled={busy || page <= 1} onClick={() => onPageChange(page - 1)} aria-label="หน้าก่อนหน้า">
            <Icon name="chevron_left" size={18} />
          </button>
          <span className="px-[var(--space-2)] text-[var(--ink-900)]">
            หน้า <span className="bo-num font-[var(--weight-semibold)]">{fmt(page)}</span> / <span className="bo-num">{fmt(lastPage)}</span>
          </span>
          <button type="button" className={btn} disabled={busy || page >= lastPage} onClick={() => onPageChange(page + 1)} aria-label="หน้าถัดไป">
            <Icon name="chevron_right" size={18} />
          </button>
          <button type="button" className={btn} disabled={busy || page >= lastPage} onClick={() => onPageChange(lastPage)} aria-label="หน้าสุดท้าย">
            <Icon name="last_page" size={18} />
          </button>
        </div>
      </div>
    </nav>
  );
};
